import distance from "@turf/distance";
import rhumbBearing from "@turf/rhumb-bearing";
import rhumbDestination from "@turf/rhumb-destination";
import GeoJSON from "geojson";
import { FixedShapeSupportedGeometry } from "../types/internal";
import { ensure360Bearing, getCoordinateList, numbersEqualWithin, updateCoordinates } from "./geojson-helpers";
import { getShapeCenter } from "./circle-geojson-helpers";

const TRIANGLE_ANGLES = [0, 120, 240] as const;

/** Creates regular triangle coordinates from a center and radius, returning 4 coordinates (3 + closing). */
export function createTriangleFromCenterAndRadius(center: GeoJSON.Position, radius: number, mapBearing = 0): GeoJSON.Position[] {
    const coords = TRIANGLE_ANGLES.map((angle) => rhumbDestination(center, radius, ensure360Bearing(mapBearing + angle), { units: "meters" }).geometry.coordinates);
    return [...coords, coords[0]];
}

/** Gets one edge length of a regular triangle geometry in meters. */
export function getTriangleSideLength(geometry: FixedShapeSupportedGeometry): number {
    const coords = getCoordinateList(geometry);
    if (coords.length < 2) return 0;
    return distance(coords[0], coords[1], { units: "meters" });
}

/** Resizes a regular triangle during drawing - determines the radius from center to target coordinate. */
export function resizeTriangle(currentGeom: FixedShapeSupportedGeometry, targetCoord: GeoJSON.Position, mapBearing = 0): FixedShapeSupportedGeometry {
    const listOfCoordinates = getCoordinateList(currentGeom);
    if (currentGeom.coordinates.length === 0 || listOfCoordinates.every((coord) => numbersEqualWithin(targetCoord, coord, 0))) {
        return {
            ...currentGeom,
            coordinates: [currentGeom.type === "Polygon" ? [targetCoord, targetCoord] : targetCoord],
        } as FixedShapeSupportedGeometry;
    }

    const center = getShapeCenter(currentGeom);
    const radius = distance(center, targetCoord, { units: "meters" });
    const coords = createTriangleFromCenterAndRadius(center, radius, mapBearing);
    return updateCoordinates(currentGeom, coords) as FixedShapeSupportedGeometry;
}

/** Resizes a regular triangle by setting a new radius value while preserving current orientation. */
export function resizeTriangleByRadius(geometry: FixedShapeSupportedGeometry, newRadius: number): FixedShapeSupportedGeometry {
    const center = getShapeCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    const bearingOfTriangle = firstVertex ? ensure360Bearing(rhumbBearing(center, firstVertex)) : 0;
    return updateCoordinates(geometry, createTriangleFromCenterAndRadius(center, newRadius, bearingOfTriangle));
}

/** Resizes a regular triangle by setting a new side length value. */
export function resizeTriangleBySideLength(geometry: FixedShapeSupportedGeometry, newSideLength: number): FixedShapeSupportedGeometry {
    // For an equilateral triangle: side length s = sqrt(3) * circumradius R.
    const radius = newSideLength / Math.sqrt(3);
    return resizeTriangleByRadius(geometry, radius);
}

/** Resizes a regular triangle when a node is dragged, preserving current triangle orientation. */
export function resizeTriangleFromNodeDrag(geometry: FixedShapeSupportedGeometry, newCoordinate: GeoJSON.Position): FixedShapeSupportedGeometry {
    const center = getShapeCenter(geometry);
    const coords = getCoordinateList(geometry);
    const firstVertex = coords[0];
    const bearingOfTriangle = firstVertex ? ensure360Bearing(rhumbBearing(center, firstVertex)) : 0;
    const newRadius = distance(center, newCoordinate, { units: "meters" });
    return updateCoordinates(geometry, createTriangleFromCenterAndRadius(center, newRadius, bearingOfTriangle));
}
